import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';


// Helper function to format text content
const formatTextContent = (text: string | null | undefined): React.ReactNode => {
  if (!text) return null;

  // Ensure we have a string
  const textStr = String(text);

  // Remove HTML tags and decode HTML entities
  let cleanText = textStr.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = cleanText;
  cleanText = textarea.value;

  // Handle truncation indicator
  if (cleanText.endsWith('...') && cleanText.length < 500) {
    // This might be truncated text, display it as is
    return (
      <div className="text-sm text-gray-700">
        <p>{cleanText}</p>
        <p className="text-xs text-gray-500 italic mt-2">
          Note: This text may be truncated. View the full PDF for complete details.
        </p>
      </div>
    );
  }

  // Split by double newlines or common section headers
  const sections = cleanText.split(/\n\n+|\n(?=(?:Summary|The complaint|What happened|Reasoning|Final decision|My final decision|Background|Decision|Outcome)[\s:])/i);

  return sections.map((section, index) => {
    const trimmedSection = section.trim();

    if (!trimmedSection) return null;

    // Check if this is a section header
    const headerMatch = trimmedSection.match(/^(Summary|The complaint|What happened|Reasoning|Final decision|My final decision|Background|Decision|Outcome)[\s:]*/i);
    if (headerMatch) {
      const headerText = headerMatch[1];
      const content = trimmedSection.substring(headerMatch[0].length).trim();

      return (
        <div key={index} className="mb-4">
          <h5 className="text-sm font-semibold text-gray-900 mb-2">{headerText}</h5>
          {content && (
            <div className="text-sm text-gray-700 leading-relaxed">
              {formatSectionContent(content)}
            </div>
          )}
        </div>
      );
    }

    // Check if this looks like a list
    const isListItem = trimmedSection.startsWith('•') ||
                       trimmedSection.startsWith('-') ||
                       /^\d+\./.test(trimmedSection);

    if (isListItem) {
      const items = trimmedSection.split('\n').filter(item => item.trim());
      return (
        <ul key={index} className="list-disc list-inside mb-3 space-y-1">
          {items.map((item, itemIndex) => (
            <li key={itemIndex} className="text-sm text-gray-700">
              {item.replace(/^[•\-\d+\.\s]+/, '')}
            </li>
          ))}
        </ul>
      );
    }

    // Regular paragraph
    return formatSectionContent(trimmedSection, index);
  }).filter(Boolean);
};

// Helper to format section content
const formatSectionContent = (content: string, key?: number | string): React.ReactNode => {
  // Split very long content into readable paragraphs
  if (content.length > 500) {
    // Try to split on sentence boundaries
    const sentences = content.match(/[^.!?]+[.!?]+/g) || [content];
    const paragraphs = [];
    let currentParagraph = '';

    for (const sentence of sentences) {
      if ((currentParagraph + sentence).length > 400 && currentParagraph) {
        paragraphs.push(currentParagraph.trim());
        currentParagraph = sentence.trim();
      } else {
        currentParagraph += (currentParagraph ? ' ' : '') + sentence.trim();
      }
    }
    if (currentParagraph) {
      paragraphs.push(currentParagraph.trim());
    }

    return (
      <div key={key} className="space-y-2">
        {paragraphs.map((para, idx) => (
          <p key={idx} className="text-sm text-gray-700 leading-relaxed">
            {para}
          </p>
        ))}
      </div>
    );
  }

  // Single paragraph
  return (
    <p key={key} className="text-sm text-gray-700 leading-relaxed mb-3">
      {content}
    </p>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  decisionId: string | null;
  loading?: boolean;
  decision?: any | null; // Using any to avoid tight coupling; shape is stable in API
  error?: string | null;
};

const FOSDecisionDetailModal: React.FC<Props> = ({ open, onClose, decisionId, loading, decision, error }) => {
  // Use decision reference if available, otherwise use decisionId
  const displayId = decision?.reference || decisionId || 'Unknown';
  const pdfUrl = decision?.pdf_url || (decisionId ? `https://www.financial-ombudsman.org.uk/decision/${decisionId}.pdf` : '');

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel className="w-[33vw] min-w-[500px] transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl">
                <div className="p-6">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    FOS Decision {displayId}
                  </Dialog.Title>
                  {decision && (
                    <div className="mt-1 text-sm text-gray-500">{decision.decision_status} • {decision.industry_sector}</div>
                  )}

                  <div className="mt-4 space-y-4">
                    {loading && (
                      <div className="text-gray-600">Loading decision…</div>
                    )}

                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-800 font-medium">Error loading decision</p>
                        <p className="text-sm text-red-600 mt-1">{error}</p>
                      </div>
                    )}

                    {decision && (
                      <>
                        {/* Display full text if available, otherwise show sections */}
                        {decision.full_text ? (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Full Decision</h4>
                            <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                              {formatTextContent(decision.full_text)}
                            </div>
                          </div>
                        ) : (
                          <>
                            {decision.summary && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Summary</h4>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  {formatTextContent(decision.summary)}
                                </div>
                              </div>
                            )}
                            {decision.complaint_section && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">The Complaint</h4>
                                <div className="bg-white border border-gray-200 rounded-lg p-3">
                                  {formatTextContent(decision.complaint_section)}
                                </div>
                              </div>
                            )}
                            {decision.what_happened_section && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">What Happened</h4>
                                <div className="bg-white border border-gray-200 rounded-lg p-3">
                                  {formatTextContent(decision.what_happened_section)}
                                </div>
                              </div>
                            )}
                            {decision.reasoning_section && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Reasoning</h4>
                                <div className="bg-white border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
                                  {formatTextContent(decision.reasoning_section)}
                                </div>
                              </div>
                            )}
                            {decision.final_decision_section && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Final Decision</h4>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  {formatTextContent(decision.final_decision_section)}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                    <div className="pt-3 flex items-center justify-between border-t border-gray-200">
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Open PDF
                      </a>
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:text-gray-900 hover:border-gray-400"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default FOSDecisionDetailModal;

